import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  FETCHER_CAPABILITY,
  FETCHER_VIEW_PERMISSION,
} from "../helpers/fetcherPermissions";
import {
  parseDuplicateOf,
  rowId,
  selectCatalogueRows,
} from "../helpers/fetcherCatalogue";
import { SortDirection } from "../helpers/tableSorting";
import {
  readPageParam,
  readRowsPerPageParam,
  readSearchParam,
  writePageParam,
  writeRowsPerPageParam,
  writeSearchParam,
} from "../helpers/urlParams";
import DeleteCatalogueItemModal from "../DeleteCatalogueItemModal";
import ConfigBrandsModal from "./ConfigBrandsModal";
import ConfigsFilterCard from "./ConfigsFilterCard";
import ConfigsSkeleton from "./ConfigsSkeleton";
import ConfigsTable, { ConfigTableRow } from "./ConfigsTable";
import CreateConfigModal from "./CreateConfigModal";
import EditConfigModal from "./EditConfigModal";
import ViewConfigModal from "./ViewConfigModal";
import { ConfigDraft } from "./ConfigDefinitionFields";

const DEFAULT_SORT_KEY = "id";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

const Configs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const canEdit = CheckPermissions(
    userData?.data?.products?.data_collection,
    FETCHER_CAPABILITY,
    FETCHER_VIEW_PERMISSION,
  );

  const { fetcherSettingsMainTablesData, isCatalogueLoading } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  // the catalogue query returns every level in one list; this tab wants the
  // config rows
  const configRows = useMemo(
    () => selectCatalogueRows(fetcherSettingsMainTablesData, "config"),
    [fetcherSettingsMainTablesData],
  );

  const selectedName = readSearchParam(searchParams.get("search"));
  const page = readPageParam(searchParams.get("page"));
  const rowsPerPage = readRowsPerPageParam(searchParams.get("page_size"));
  const [selectedInterfaces, setSelectedInterfaces] = useState<string[]>([]);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION,
  );
  const [brandsModalId, setBrandsModalId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [createDraft, setCreateDraft] = useState<ConfigDraft | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editing, setEditing] = useState<{
    id: string;
    mode: "view" | "edit";
  } | null>(null);

  // the same name can be used by more than one config, so the options are deduped
  const searchOptions = useMemo(
    () =>
      [
        ...new Set(
          configRows
            .map((row) => row.item_name)
            .filter((name): name is string => !!name),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [configRows],
  );

  // both filter lists come from the config rows themselves, so every option
  // offered selects at least one row
  const interfaceOptions = useMemo(() => {
    const byId = new Map<string, string>();
    configRows.forEach((row) => {
      const id = rowId(row.interface_id);
      if (id && !byId.has(id)) {
        byId.set(id, `${row.interface_name ?? "?"} (${id})`);
      }
    });
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [configRows]);

  const reportOptions = useMemo(() => {
    const byId = new Map<string, string>();
    configRows.forEach((row) => {
      const id = rowId(row.report_id);
      if (id && !byId.has(id)) {
        byId.set(id, `${row.report_name ?? "?"} (${id})`);
      }
    });
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [configRows]);

  const rows: ConfigTableRow[] = useMemo(
    () =>
      configRows
        .filter(
          (row) =>
            (!selectedName || row.item_name === selectedName) &&
            (selectedInterfaces.length === 0 ||
              selectedInterfaces.includes(rowId(row.interface_id))) &&
            (selectedReports.length === 0 ||
              selectedReports.includes(rowId(row.report_id))),
        )
        .map((row) => ({
          id: rowId(row.item_id),
          name: row.item_name ?? "",
          interfaceName: row.interface_name ?? "-",
          // pre-composed in SQL - shown as delivered, never rebuilt here
          recipe: row.recipe ?? "N/A",
          used: row.used_by_brands ?? 0,
          twin: parseDuplicateOf(row.duplicate_of),
          modified: row.modified ?? "",
        })),
    [configRows, selectedName, selectedInterfaces, selectedReports],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
    writePageParam(setSearchParams, 0);
  };

  const openDuplicate = (configId: string) => {
    const config = configRows.find((row) => rowId(row.item_id) === configId);
    if (!config) return;
    setEditing(null);
    // the recipe is copied by its ids, which is what the create form binds to
    setCreateDraft({
      name: `${config.item_name ?? ""}_copy`,
      report: rowId(config.report_id),
      cm: rowId(config.collection_method_id),
      sfm: rowId(config.sub_filtering_method_id),
      ft: rowId(config.format_type_id),
    });
    setIsCreateOpen(true);
  };

  return (
    <>
      <ConfigsFilterCard
        searchOptions={searchOptions}
        selectedName={selectedName}
        onSelectName={(value) => writeSearchParam(setSearchParams, value)}
        interfaceOptions={interfaceOptions}
        selectedInterfaces={selectedInterfaces}
        onSelectedInterfacesChange={setSelectedInterfaces}
        reportOptions={reportOptions}
        selectedReports={selectedReports}
        onSelectedReportsChange={setSelectedReports}
        onClearFilters={() => {
          setSelectedInterfaces([]);
          setSelectedReports([]);
        }}
        canCreate={canEdit}
        onCreate={() => {
          setCreateDraft(null);
          setIsCreateOpen(true);
        }}
      />

      {isCatalogueLoading ? (
        <ConfigsSkeleton />
      ) : (
        <ConfigsTable
          rows={rows}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(value) => writePageParam(setSearchParams, value)}
          onRowsPerPageChange={(value) =>
            writeRowsPerPageParam(setSearchParams, value)
          }
          canEdit={canEdit}
          onView={(id) => setEditing({ id, mode: "view" })}
          onEdit={(id) => setEditing({ id, mode: canEdit ? "edit" : "view" })}
          onDuplicate={openDuplicate}
          onDelete={setDeleteId}
          onShowBrands={setBrandsModalId}
          onOpenTwin={(id) => setEditing({ id, mode: "view" })}
        />
      )}

      <CreateConfigModal
        open={isCreateOpen}
        initial={createDraft}
        onClose={() => setIsCreateOpen(false)}
      />

      {editing?.mode === "view" && (
        <ViewConfigModal
          open
          configId={editing.id}
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
          onOpenTwin={(id) => setEditing({ id, mode: "view" })}
          canEdit={canEdit}
        />
      )}

      {editing?.mode === "edit" && (
        <EditConfigModal
          open
          configId={editing.id}
          mode="edit"
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
        />
      )}

      {brandsModalId && (
        <ConfigBrandsModal
          open
          configId={brandsModalId}
          onClose={() => setBrandsModalId(null)}
        />
      )}

      {deleteId && (
        <DeleteCatalogueItemModal
          open
          level="config"
          itemId={deleteId}
          onClose={() => setDeleteId(null)}
        />
      )}
    </>
  );
};

export default Configs;
